/**
 * Analytics tracking service
 * Simple client-side tracking using Google Tag Manager
 * 
 * NOTE: For PostHog tracking, use the useTracking() hook from hooks/useTracking.ts
 * This ensures proper initialization via the PostHogProvider React context.
 * 
 * No backend required - all events go to GTM dataLayer
 */

// Declare dataLayer for GTM
declare global {
  interface Window {
    dataLayer?: any[];
  }
}

/**
 * Get cookie value
 */
export function getCookie(name: string): string {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || '';
  return '';
}

/**
 * Generate unique event ID
 */
function generateEventId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Track events to Google Tag Manager and PostHog
 * Simple client-side tracking - no backend required
 */
export function trackEvent(eventName: string, eventData: Record<string, any> = {}): void {
  if (typeof window === 'undefined') {
    return; // Server-side rendering guard
  }

  // Initialize dataLayer if needed
  window.dataLayer = window.dataLayer || [];
  
  // Generate unique event ID if not provided
  const eventId = eventData.event_id || generateEventId();
  
  // Get Facebook cookies
  const fbp = getCookie('_fbp') || '';
  const fbc = getCookie('_fbc') || '';
  
  // Get user ID from event data
  const userId = eventData.user_id || eventData.external_id || '';
  const externalId = eventData.external_id || userId || '';
  
  // Build payload
  const payload: Record<string, any> = {
    event: eventName,
    event_id: eventId,
    external_id: externalId,
    fbp: fbp,
    ...eventData, // Include all event-specific data
  };
  
  // Add user_id if available
  if (userId) {
    payload.user_id = userId;
  }
  
  // Add fbc only if present
  if (fbc) {
    payload.fbc = fbc;
  }
  
  // Remove empty values (except fbp which should always be included)
  Object.keys(payload).forEach(key => {
    if (key !== 'fbp' && key !== 'event' && (payload[key] === undefined || payload[key] === null || payload[key] === '')) {
      delete payload[key];
    }
  });
  
  // Push to dataLayer (GTM)
  window.dataLayer.push(payload);

  // NOTE: PostHog tracking should be done via useTracking() hook
  // This ensures proper initialization via PostHogProvider React context
  // Direct PostHog import here won't work because PostHog is initialized
  // via the React provider, not as a standalone instance
}